import { useCallback } from 'react'

import type { Moment } from 'moment'
import { DriverCalendarDayModel } from 'app/models/calendar/driverCalendarDayModel'
import { DayType } from 'app/constants/dayType'
import { AttestationModel } from '@/app/models/attestation/attestationModel'
import useCalendarService from '@/modules/drivers/services/CalendarService'
import { toast } from '@/lib/toasts/api'
import { notifyOperationResults } from '@/modules/shared/lib/operationResults'
import { useLocalize } from '@/modules/shared/context/localize/LocalizeContext'

const formatDateToYYYYMMDD = (date: string | Moment): string => {
  const dateString = typeof date === 'string' ? date : date.format()
  return dateString.split('T')[0]
}

export const useDayEvents = () => {
  const { t } = useLocalize()
  const calendarService = useCalendarService()
  const handleGoToDayChart = useCallback(
    (history, day: DriverCalendarDayModel) => {
      const formattedDate = formatDateToYYYYMMDD(day.activityDayLocalTimeDate)

      const currentPath = history.location.pathname
      const newPath = currentPath.replace(
        /yearlyActivity\/.*/,
        `dailyActivity/${formattedDate}`,
      )
      history.push(newPath)
    },
    [],
  )

  const handleGoToWeekChart = useCallback(
    (history, day: DriverCalendarDayModel) => {
      const formattedDate = formatDateToYYYYMMDD(day.activityDayLocalTimeDate)

      const currentPath = history.location.pathname
      const newPath = currentPath.replace(
        /yearlyActivity\/.*/,
        `weeklyActivity/${formattedDate}`,
      )
      history.push(newPath)
    },
    [],
  )

  const handleShowDetails = useCallback((day: DriverCalendarDayModel) => {
    console.log('handleShowDetails')
  }, [])

  const handleShowChart = useCallback(
    (
      day: DriverCalendarDayModel,

      currentSelectedDay: DriverCalendarDayModel | null,
      setSelectedDay: (day: DriverCalendarDayModel | null) => void,
    ) => {
      if (
        currentSelectedDay &&
        day.activityDayLocalTimeDate.format('YYYY-MM-DD') ===
          currentSelectedDay.activityDayLocalTimeDate.format('YYYY-MM-DD')
      ) {
        setSelectedDay(null)
      } else {
        setSelectedDay(day)
      }
    },
    [],
  )

  const handleGenerateAttestation = useCallback(
    async (companyId: string, attestation: AttestationModel) => {
      try {
        await calendarService.generateAttestation(companyId, attestation)
        toast.success({
          text: t('driverCalendar.attestation-generated'),
        })
      } catch (error) {
        console.error('Error generating attestation:', error)
        toast.error({
          text: t('driverCalendar.attestation-generation-error'),
        })
      }
    },
    [calendarService, t],
  )

  const handleRestoreOriginalCardData = useCallback(
    async (
      companyId: string,
      id: number | string,
      dates: string[],
      type: 'vehicle' | 'driver',
    ) => {
      try {
        await calendarService.restoreOriginalCardData(
          companyId,
          String(id),
          dates,
          type,
        )
        toast.success({
          text: t('driverCalendar.original-card-data-restored'),
        })
      } catch (error) {
        console.error('Error restoring original card data:', error)
        toast.error({
          text: t('driverCalendar.restore-card-data-error'),
        })
      }
    },
    [calendarService, t],
  )

  const handleRemoveDigitalData = useCallback(
    async (
      companyId: string,
      id: number | string,
      dates: string[],
      type: 'vehicle' | 'driver',
    ) => {
      try {
        await calendarService.removeDigitalData(
          companyId,
          String(id),
          dates,
          type,
        )
        toast.success({
          text: t('driverCalendar.digital-data-removed'),
        })
      } catch (error) {
        console.error('Error removing digital data:', error)
        toast.error({
          text: t('driverCalendar.remove-digital-data-error'),
        })
      }
    },
    [calendarService, t],
  )
  const handleRemoveImportedVehicleActivity = useCallback(
    async (companyId: string, id: number | string, dates: string[]) => {
      try {
        await calendarService.removeImportedActivities(
          companyId,
          String(id),
          dates,
        )
        toast.success({
          text: t('driverCalendar.digital-data-removed'),
        })
      } catch (error) {
        console.error('Error removing imported data:', error)
        toast.error({
          text: t('driverCalendar.remove-digital-data-error'),
        })
      }
    },
    [calendarService, t],
  )
  const handleInsertActivityFromAnotherDay = useCallback(
    async (
      companyId: string,
      selectedDriverId: number,
      selectedDates: Array<string>,
      targetDriverId: number,
      targetDate: string,
    ) => {
      try {
        const res = await calendarService.copyActivitiesFromAnotherDay(
          companyId,
          String(selectedDriverId),
          selectedDates,
          String(targetDriverId),
          targetDate,
        )
        if (res?.data && Array.isArray(res.data) && res.data.length === 0) {
          toast.warning({
            text: t('driverCalendar.no-activities-to-copy'),
          })
          return
        }
        toast.success({
          text: t('driverCalendar.activities-copied-from-another-day'),
        })
      } catch (error) {
        console.error('Error copying activities:', error)
        toast.error({
          text: t('driverCalendar.copy-activities-error'),
        })
      }
    },
    [calendarService, t],
  )

  const handleAssignBorderCrossingsFromVehicle = useCallback(
    async (
      companyId: string,
      id: number | string,
      dates: string[],
      type: 'vehicle' | 'driver',
    ) => {
      try {
       const res = await calendarService.assignBorderCrossingsFromVehicle(
          companyId,
          String(id),
          dates,
          type,
        )
        if (res?.data && Array.isArray(res.data) && res.data.length === 0) {
          toast.warning({
            text: t('driverCalendar.no-border-crossing-data'),
          })
          return
        }
        toast.success({
          text: t('driverCalendar.border-crossings-assigned'),
        })
      } catch (error) {
        console.error('Error assigning border crossings:', error)
        toast.error({
          text: t('driverCalendar.assign-border-crossings-error'),
        })
      }
    },
    [calendarService, t],
  )

  const handleAutoAssignBorderCrossingsToDriver = useCallback(
    async (
      companyId: string,
      id: number | string,
      dates: string[],
      type: 'vehicle' | 'driver',
    ) => {
      try {
        const res = await calendarService.autoAssignBorderCrossingsToDriver(
          companyId,
          String(id),
          dates,
          type,
        )
        notifyOperationResults(res?.data, t, {
          success: 'driverCalendar.border-crossings-assigned',
          warning: 'driverCalendar.no-border-crossing-data',
          error: 'driverCalendar.assign-border-crossings-error',
        })
      } catch (error) {
        console.error('Error auto-assigning border crossings:', error)
        toast.error({
          text: t('driverCalendar.assign-border-crossings-error'),
        })
      }
    },
    [calendarService, t],
  )

  const handleRemoveBorderCrossings = useCallback(
    async (
      companyId: string,
      id: number | string,
      dates: string[],
      type: 'vehicle' | 'driver',
    ) => {
      try {
        await calendarService.removeBorderCrossings(
          companyId,
          String(id),
          dates,
          type,
        )
        toast.success({
          text: t('driverCalendar.border-crossings-removed'),
        })
      } catch (error) {
        console.error('Error removing border crossings:', error)
        toast.error({
          text: t('driverCalendar.remove-border-crossings-error'),
        })
      }
    },
    [calendarService, t],
  )

  const handleAssignTmsDataToDriverAutomatically = useCallback(
    async (
      companyId: string,
      id: number | string,
      dates: string[],
      type: 'vehicle' | 'driver',
    ) => {
      try {
        const res = await calendarService.autoAssignTmsDataToDriver(
          companyId,
          String(id),
          dates,
          type,
        )
        notifyOperationResults(res?.data, t, {
          success: 'driverCalendar.tms-data-auto-assigned',
          warning: 'driverCalendar.no-tms-data-to-assign',
          error: 'driverCalendar.auto-assign-tms-error',
        })
      } catch (error) {
        console.error('Error auto-assigning TMS data:', error)
        toast.error({
          text: t('driverCalendar.auto-assign-tms-error'),
        })
      }
    },
    [calendarService, t],
  )

  const handleRemoveTmsData = useCallback(
    async (
      companyId: string,
      id: number | string,
      dates: string[],
      type: 'vehicle' | 'driver',
    ) => {
      try {
        await calendarService.removeTmsData(companyId, String(id), dates, type)
        toast.success({
          text: t('driverCalendar.tms-data-removed'),
        })
      } catch (error) {
        console.error('Error removing TMS data:', error)
        toast.error({
          text: t('driverCalendar.remove-tms-data-error'),
        })
      }
    },
    [calendarService, t],
  )

  const handlePrintApplications = useCallback((day: DriverCalendarDayModel) => {
    console.log('handlePrintApplications')
  }, [])

  const handleChangeDayType = useCallback(
    async (
      day: DriverCalendarDayModel,
      dayType: DayType,
      companyId: string,
      driverId: number,
      dates: string[],
      changeDayTypesFunc: (
        companyId: string,
        driverId: number,
        dates: string[],
        dayType: DayType,
        note: string | null,
      ) => Promise<any>,
    ) => {
      try {
        await changeDayTypesFunc(companyId, driverId, dates, dayType, null)
        toast.success({
          text: t('driverCalendar.day-type-changed'),
        })
      } catch (error) {
        console.error('Error changing day type:', error)
        toast.error({
          text: t('driverCalendar.change-day-type-error'),
        })
      }
    },
    [t],
  )

  const handleRestoreDefaultDayType = useCallback(
    async (
      companyId: string,
      driverId: number,
      dates: Array<string>,
      changeDayTypesFunc: any,
    ) => {
      try {
        await changeDayTypesFunc(companyId, driverId, dates, 0, null)
        toast.success({
          text: t('driverCalendar.default-day-type-restored'),
        })
      } catch (error) {
        console.error('Error restoring default day type:', error)
        toast.error({
          text: t('driverCalendar.restore-default-day-type-error'),
        })
      }
    },
    [t],
  )

  return {
    handleGoToDayChart,
    handleGoToWeekChart,
    handleShowDetails,
    handleShowChart,
    handleGenerateAttestation,
    handleRestoreOriginalCardData,
    handleRemoveDigitalData,
    handleInsertActivityFromAnotherDay,
    handleAssignBorderCrossingsFromVehicle,
    handleAutoAssignBorderCrossingsToDriver,
    handleRemoveBorderCrossings,
    handleAssignTmsDataToDriverAutomatically,
    handleRemoveTmsData,
    handlePrintApplications,
    handleChangeDayType,
    handleRestoreDefaultDayType,
    handleRemoveImportedVehicleActivity
  }
}
